import { memo, useCallback, useEffect } from "react";
import type { ReactNode, SyntheticEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import OnboardingTooltip from "components/OnboardingTooltip";
import {
  OnboardingTooltipBadgeTitle,
  OnboardingTooltipCloseButton,
  OnboardingTooltipContent,
  OnboardingTooltipDescription,
  OnboardingTooltipHeader,
  OnboardingTooltipLink,
} from "components/OnboardingTooltip/components";
import Bold from "components/primitives/Bold";
import Div from "components/primitives/Div";
import { linkVelocityBasedOneRepMax } from "config/knowledgebase-links";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import useVelocityOnBoarding from "hooks/useVelocityOnBoarding";
import logEvent from "utils/logEvent";

function ProfilingVelocityOnboardingTooltipTitle() {
  const { t } = useTranslation();

  const handleLearnMoreClick = useCallback(
    () => logEvent("ATHLETE_PROFILE_PROFILING_VELOCITY_ONBOARDING_LEARN_MORE_CLICK"),
    []
  );

  return (
    <OnboardingTooltipContent>
      <OnboardingTooltipHeader>
        <OnboardingTooltipBadgeTitle>
          {t("athlete_profile.profiling_velocity_onboarding_title")}
        </OnboardingTooltipBadgeTitle>
        <OnboardingTooltipCloseButton />
      </OnboardingTooltipHeader>
      <OnboardingTooltipDescription component="div" sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <span>
          <Trans i18nKey="athlete_profile.profiling_velocity_onboarding_description" components={{ Bold: <Bold /> }}>
            Automatically <Bold>track your athlete’s latest profile using qualifying VBT</Bold> sets from the past 42
            days.
          </Trans>
        </span>
        <span>{t("athlete_profile.profiling_velocity_onboarding_comparison")}</span>
      </OnboardingTooltipDescription>
      <OnboardingTooltipLink href={linkVelocityBasedOneRepMax} onClick={handleLearnMoreClick} />
    </OnboardingTooltipContent>
  );
}

interface ProfilingVelocityOnboardingTooltipProps {
  children: ReactNode;
  disabled?: boolean;
}

/** Introduces the latest load velocity profile, pointing at the baseline sets selector. */
function ProfilingVelocityOnboardingTooltip(props: ProfilingVelocityOnboardingTooltipProps) {
  const { t } = useTranslation();
  const { open, dismiss } = useVelocityOnBoarding(LocalStorageHelper.keys.showAthleteProfilingVelocityTooltip);
  const isOpen = open && !props.disabled;

  useEffect(() => {
    if (!isOpen) return;

    logEvent("ATHLETE_PROFILE_PROFILING_VELOCITY_ONBOARDING_VIEW");
  }, [isOpen]);

  const handleClose = useCallback(
    (event: SyntheticEvent | Event) => {
      logEvent("ATHLETE_PROFILE_PROFILING_VELOCITY_ONBOARDING_DISMISS", {
        origin: event.type === "keydown" ? "escapeKey" : "closeButton",
      });
      dismiss();
    },
    [dismiss]
  );

  return (
    <OnboardingTooltip
      title={<ProfilingVelocityOnboardingTooltipTitle />}
      placement="top"
      open={isOpen}
      feature={t("global.load_velocity_profile_label")}
      onClose={handleClose}
      sx={{ width: "420px" }}
    >
      <Div>{props.children}</Div>
    </OnboardingTooltip>
  );
}

export default memo(ProfilingVelocityOnboardingTooltip);
